import React, { useState, useEffect } from 'react';
import { Menu, X, Search, ChevronDown } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { t, getCurrentLanguage } from '../utils/translations';
import LanguageSwitcher from './LanguageSwitcher';
import i18n from '../i18n';
import api from './api';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedDropdowns, setExpandedDropdowns] = useState({});
  const [currentLanguage, setCurrentLanguage] = useState(getCurrentLanguage());
  const [slides, setSlides] = useState([]);
  const navigate = useNavigate();
  const location = useLocation();
  const currentPath = location.pathname;

  // Récupérer les slides depuis l'API
  useEffect(() => {
    const fetchSlides = async () => {
      try {
        const response = await api.get('/api/hero-slides/');
        setSlides(response.data);
      } catch (err) {
        console.error('Erreur lors du chargement des slides:', err);
        setSlides([]);
      }
    };
    fetchSlides();
  }, []);

  // Écouter les changements de langue
  useEffect(() => {
    const handleLanguageChange = () => setCurrentLanguage(getCurrentLanguage());
    i18n.on('languageChanged', handleLanguageChange);
    window.addEventListener('languageChanged', handleLanguageChange);
    return () => {
      i18n.off('languageChanged', handleLanguageChange);
      window.removeEventListener('languageChanged', handleLanguageChange);
    };
  }, []);

  // Empêcher le scroll de la page quand le menu mobile est ouvert
  useEffect(() => {
    document.body.style.overflow = isOpen ? 'hidden' : 'unset';
    return () => { document.body.style.overflow = 'unset'; };
  }, [isOpen]);

  const isActivePath = (href) => {
    const normalizedHref = href.replace(`/${currentLanguage}`, '');
    const normalizedPath = currentPath.replace(`/${currentLanguage}`, '');
    if (href.includes('/slide/')) return currentPath === href;
    return currentPath === href || normalizedPath.endsWith(normalizedHref);
  };

  const handleNavigation = (path) => {
    window.scrollTo(0, 0);
    if (path === `/${currentLanguage}` && currentPath === `/${currentLanguage}`) return;

    let fullPath = path;
    if (!path.startsWith('/fr/') && !path.startsWith('/en/') && path !== '/') {
      fullPath = `/${currentLanguage}${path}`;
    }
    if (fullPath.startsWith(`/${currentLanguage}/${currentLanguage}`)) {
      fullPath = fullPath.replace(`/${currentLanguage}/${currentLanguage}`, `/${currentLanguage}`);
    }

    setIsOpen(false);
    setExpandedDropdowns({});
    setTimeout(() => navigate(fullPath), 0);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      handleNavigation(`/actualites?q=${encodeURIComponent(searchQuery.trim())}`);
    }
  };

  const toggleDropdown = (key) => {
    setExpandedDropdowns(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const generateActionLinks = () => {
    if (slides.length === 0) return [];
    return slides
      .sort((a, b) => a.order - b.order)
      .map((slide) => {
        const title = currentLanguage === 'en'
          ? (slide.title_en || slide.title_fr)
          : (slide.title_fr || slide.title_en);
        return {
          title,
          href: `/${currentLanguage}/full-description/${slide.slug}`
        };
      });
  };

  const menuItems = React.useMemo(() => [
    { title: t('nav.discover'), href: `/${currentLanguage}/about`, hasDropdown: true, key: 'discover', dropdownItems: [
      { title: t('nav.about'), href: `/${currentLanguage}/about`, key: 'about' },
      { title: t('nav.mission'), href: `/${currentLanguage}/mission`, key: 'mission' },
      { title: t('nav.history'), href: `/${currentLanguage}/historique`, key: 'history' },
      { title: t('nav.team'), href: `/${currentLanguage}/equipe`, key: 'team' },
      { title: t('nav.status'), href: '/STATUTS_AHB.pdf', key: 'status', isExternal: true }
    ]},
    { title: t('nav.actions'), href: '#', hasDropdown: true, key: 'actions', dropdownItems: generateActionLinks() },
    { title: t('nav.news'), href: `/${currentLanguage}/actualites`, key: 'news' },
    { title: t('nav.projects'), href: '#', hasDropdown: true, key: 'projects', dropdownItems: [
      { title: t('nav.project_health'), href: `/${currentLanguage}/projet/sante-et-securite`, key: 'project_health' },
      { title: t('nav.project_water'), href: `/${currentLanguage}/projet/eau-et-assainissement`, key: 'project_water' },
      { title: t('nav.project_environment'), href: `/${currentLanguage}/projet/environnement`, key: 'project_environment' },
      { title: t('nav.project_education'), href: `/${currentLanguage}/projet/ahb-education`, key: 'project_education' }
    ]},
    { title: t('nav.development'), href: `/${currentLanguage}/developpement`, key: 'development' },
    { title: t('nav.membership'), href: `/${currentLanguage}/adhesion`, key: 'membership' },
    { title: t('nav.contacts'), href: `/${currentLanguage}/contact`, key: 'contacts' }
  ], [currentLanguage, slides]);

  return (
    <nav className="w-full sticky top-0 z-50 bg-white shadow-sm">

      {/* ── ROW 1 : LOGO + SEARCH + LANG ── */}
      <div className="border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center justify-between h-24 gap-6">

            {/* Logo */}
            <button onClick={() => navigate(`/${currentLanguage}`)} className="flex items-center flex-shrink-0">
              <img src="/logo.jpg" alt="AHB Logo" className="h-20 w-auto" />
            </button>

            {/* Right : Search + Lang + Donate */}
            <div className="flex items-center gap-4 flex-shrink-0">

              {/* Search bar */}
              <form onSubmit={handleSearch} className="hidden md:flex items-center">
                <div className="relative">
                  <input
                    type="text"
                    placeholder={t('nav.search')}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-64 pl-4 pr-12 py-2 border border-gray-300 focus:outline-none focus:ring-1 focus:ring-[#089556] focus:border-[#089556] text-sm"
                  />
                  <button
                    type="submit"
                    className="absolute right-0 top-0 h-full px-3 bg-[#089556] hover:bg-[#0a7c4a] text-white transition-colors"
                    aria-label="Rechercher"
                  >
                    <Search className="h-4 w-4" />
                  </button>
                </div>
              </form>

              {/* Language Switcher */}
              <LanguageSwitcher variant="compact" className="hidden md:flex" />

              {/* Donate Button */}
              <button
                onClick={() => handleNavigation(`/${currentLanguage}/don`)}
                className="hidden sm:block bg-[#089556] text-white px-4 py-2 text-sm font-semibold hover:bg-[#0a7c4a] transition-colors uppercase tracking-wide"
              >
                {t('nav.donate')}
              </button>

              {/* Mobile Menu Button */}
              <button
                onClick={() => setIsOpen(!isOpen)}
                className="xl:hidden p-2 text-gray-700 hover:text-[#089556]"
                aria-label="Menu"
              >
                {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* ── ROW 2 : MAIN MENU (BAD-style horizontal menu) ── */}
      <div className="hidden xl:block border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center justify-between">
            {menuItems.map((item) => (
              <div key={item.key} className="relative group">
                {item.hasDropdown ? (
                  <>
                    <button className="flex items-center gap-1.5 text-gray-700 group-hover:text-[#089556] py-4 text-sm font-medium transition-colors duration-200 whitespace-nowrap border-b-2 border-transparent group-hover:border-[#089556]">
                      <span>{item.title}</span>
                      <ChevronDown className="h-3.5 w-3.5 text-[#089556]" />
                    </button>

                    {/* Dropdown */}
                    <div className="absolute left-0 top-full w-56 bg-white shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50 border-t-2 border-[#089556]">
                      <div className="py-2">
                        {item.dropdownItems.map((dropdownItem) => (
                          dropdownItem.isExternal ? (
                            <a
                              key={dropdownItem.key || dropdownItem.href}
                              href={dropdownItem.href}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="block w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 hover:text-[#089556] transition-colors"
                            >
                              {dropdownItem.title}
                            </a>
                          ) : (
                            <button
                              key={dropdownItem.key || dropdownItem.href}
                              onClick={() => handleNavigation(dropdownItem.href)}
                              className="block w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 hover:text-[#089556] transition-colors"
                            >
                              {dropdownItem.title}
                            </button>
                          )
                        ))}
                      </div>
                    </div>
                  </>
                ) : (
                  <button
                    onClick={() => handleNavigation(item.href)}
                    className={`block py-4 text-sm font-medium transition-colors duration-200 whitespace-nowrap border-b-2 ${
                      isActivePath(item.href)
                        ? 'text-[#089556] border-[#089556]'
                        : 'text-gray-700 border-transparent group-hover:text-[#089556] group-hover:border-[#089556]'
                    }`}
                  >
                    {item.title}
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── MOBILE MENU ── */}
      {isOpen && (
        <div className="xl:hidden fixed inset-0 z-50 bg-white flex flex-col">
          {/* Mobile Header */}
          <div className="border-b border-gray-200 px-4 py-4 flex-shrink-0">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <img src="/logo.jpg" alt="AHB Logo" className="h-12 w-auto" />
                <span className="text-sm text-gray-600">Faire la Différence</span>
              </div>
              <div className="flex items-center gap-2">
                <LanguageSwitcher variant="compact" />
                <button
                  onClick={() => setIsOpen(false)}
                  className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
            </div>

            <form onSubmit={handleSearch} className="relative">
              <input
                type="text"
                placeholder={t('nav.search')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-4 pr-12 py-3 border border-gray-300 focus:outline-none focus:ring-1 focus:ring-[#089556] text-sm"
              />
              <button
                type="submit"
                className="absolute right-0 top-0 h-full px-4 bg-[#089556] text-white"
              >
                <Search className="h-4 w-4" />
              </button>
            </form>
          </div>

          {/* Menu items */}
          <div className="flex-1 overflow-y-auto px-4 py-4">
            <div className="space-y-1">
              {menuItems.map((item) => (
                <div key={item.key} className="border-b border-gray-100">
                  {item.hasDropdown ? (
                    <>
                      <button
                        onClick={() => toggleDropdown(item.key)}
                        className="w-full text-left px-2 py-3 text-gray-800 hover:text-[#089556] font-medium flex items-center justify-between"
                      >
                        <span>{item.title}</span>
                        <ChevronDown
                          className={`h-4 w-4 transition-transform duration-200 ${expandedDropdowns[item.key] ? 'rotate-180' : ''}`}
                        />
                      </button>
                      {expandedDropdowns[item.key] && (
                        <div className="bg-gray-50 mb-2">
                          {item.dropdownItems.map((dropdownItem) => (
                            dropdownItem.isExternal ? (
                              <a
                                key={dropdownItem.key || dropdownItem.href}
                                href={dropdownItem.href}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="block w-full text-left px-6 py-3 text-sm text-gray-600 hover:text-[#089556]"
                              >
                                {dropdownItem.title}
                              </a>
                            ) : (
                              <button
                                key={dropdownItem.key || dropdownItem.href}
                                onClick={() => handleNavigation(dropdownItem.href)}
                                className="block w-full text-left px-6 py-3 text-sm text-gray-600 hover:text-[#089556]"
                              >
                                {dropdownItem.title}
                              </button>
                            )
                          ))}
                        </div>
                      )}
                    </>
                  ) : (
                    <button
                      onClick={() => handleNavigation(item.href)}
                      className={`block w-full text-left px-2 py-3 font-medium ${
                        isActivePath(item.href) ? 'text-[#089556]' : 'text-gray-800 hover:text-[#089556]'
                      }`}
                    >
                      {item.title}
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Donate button fixed at bottom */}
          <div className="px-4 py-4 border-t border-gray-200 flex-shrink-0">
            <button
              onClick={() => handleNavigation(`/${currentLanguage}/don`)}
              className="w-full bg-[#089556] text-white px-6 py-4 text-base font-semibold hover:bg-[#0a7c4a] transition-colors uppercase tracking-wide"
            >
              {t('nav.donate')}
            </button>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
